import { useAuth } from "@/context/AuthContext";
import { PageHeader, formatEUR, formatDate } from "@/components/app/Shared";
import { useT, useLang, LANGUAGES } from "@/lib/i18n";
import { toast } from "sonner";
import { Eye, EyeOff, Tag, Globe, Trash2, RotateCcw, Receipt, KeyRound, CreditCard, Settings as SettingsIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Billing from "@/pages/Billing";

export default function Settings() {
  const { user, settings, updateSettings, refreshUser } = useAuth();
  const { setLang } = useLang();
  const t = useT();
  const isAdmin = ["admin", "organization_admin"].includes(user?.role);
  const [params] = useSearchParams();
  const initialTab = params.get("session_id") || params.get("cancelled") ? "billing" : "preferences";
  const [tab, setTab] = useState(initialTab);

  const toggle = async (key) => {
    try {
      await updateSettings({ [key]: !settings[key] });
      toast.success("Instelling opgeslagen");
    } catch { toast.error("Opslaan mislukt"); }
  };

  const changeLanguage = async (code) => {
    setLang(code);
    try {
      await api.patch("/auth/profile", { language: code });
      await refreshUser();
      toast.success("Taal bijgewerkt");
    } catch { /* silent — local lang change still works */ }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1100px] mx-auto" data-testid="settings-page">
      <PageHeader overline="Account" title={t("settings")} description="Beheer je voorkeuren, wachtwoord en abonnement." />

      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <TabBtn active={tab === "preferences"} onClick={() => setTab("preferences")} icon={SettingsIcon} testid="tab-preferences">Voorkeuren</TabBtn>
        <TabBtn active={tab === "password"}    onClick={() => setTab("password")}    icon={KeyRound}     testid="tab-password">Wachtwoord</TabBtn>
        {isAdmin && (
          <TabBtn active={tab === "billing"}   onClick={() => setTab("billing")}     icon={CreditCard}   testid="tab-billing">Abonnement</TabBtn>
        )}
      </div>

      {tab === "preferences" && (
        <div className="space-y-3" data-testid="preferences-tab">
          <div className="card-flat p-5 flex items-start gap-4" data-testid="language-section">
            <div className="w-10 h-10 rounded-md bg-[#5BC044]/15 text-[#5BC044] flex items-center justify-center shrink-0">
              <Globe size={18} strokeWidth={1.8} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[15px] tracking-tight">{t("language")}</div>
              <p className="text-sm text-zinc-500 mt-1 mb-3">Kies de taal van de applicatie.</p>
              <div className="flex flex-wrap gap-2" data-testid="language-options">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    onClick={() => changeLanguage(l.code)}
                    className={`h-9 px-3 rounded-md border text-sm transition-colors ${user?.language === l.code ? "bg-[#5BC044] border-[#5BC044] text-white font-semibold" : "border-zinc-300 text-zinc-700 hover:border-[#5BC044] hover:text-[#5BC044]"}`}
                    data-testid={`lang-${l.code}`}
                  >
                    {l.flag} {l.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {isAdmin && (
            <>
              <div className="overline mt-6 mb-3">Chef-rechten</div>
              <SettingRow
                icon={settings.chef_show_prices ? Eye : EyeOff}
                title="Prijzen tonen aan chefs"
                desc="Wanneer uitgeschakeld, zien chefs geen prijzen of totaalbedragen."
                enabled={!!settings.chef_show_prices}
                onToggle={() => toggle("chef_show_prices")}
                testid="toggle-chef-prices"
              />
              <SettingRow
                icon={Tag}
                title="Leveranciers tonen aan chefs"
                desc="Wanneer uitgeschakeld, ziet de chef niet van welke leverancier producten komen."
                enabled={!!settings.chef_show_suppliers}
                onToggle={() => toggle("chef_show_suppliers")}
                testid="toggle-chef-suppliers"
              />

              <TrashSection />
            </>
          )}
        </div>
      )}

      {tab === "password" && <PasswordTab />}

      {tab === "billing" && isAdmin && (
        <div data-testid="billing-tab">
          <Billing embedded />
        </div>
      )}
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button onClick={onClick} className={`h-10 px-4 rounded-md text-sm font-semibold transition-colors inline-flex items-center gap-2 ${active ? "bg-[#1B2D5C] text-white" : "bg-white border border-zinc-200 text-zinc-700 hover:border-[#5BC044]"}`} data-testid={testid}>
      <Icon size={14} /> {children}
    </button>
  );
}

function SettingRow({ icon: Icon, title, desc, enabled, onToggle, testid }) {
  return (
    <div className="card-flat p-5 flex items-start gap-4">
      <div className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${enabled ? "bg-[#5BC044]/15 text-[#5BC044]" : "bg-zinc-100 text-zinc-500"}`}>
        <Icon size={18} strokeWidth={1.8} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-[15px] tracking-tight">{title}</div>
        <p className="text-sm text-zinc-500 mt-1">{desc}</p>
      </div>
      <button
        onClick={onToggle}
        className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${enabled ? "bg-[#5BC044]" : "bg-zinc-300"}`}
        data-testid={testid}
      >
        <span className={`absolute top-0.5 ${enabled ? "left-[26px]" : "left-0.5"} w-5 h-5 bg-white rounded-full shadow transition-all`} />
      </button>
    </div>
  );
}

function PasswordTab() {
  const [current, setCurrent] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) { toast.error("Nieuw wachtwoord moet minimaal 6 tekens zijn"); return; }
    if (newPw !== confirm) { toast.error("Wachtwoorden komen niet overeen"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: newPw });
      toast.success("Wachtwoord gewijzigd");
      setCurrent(""); setNewPw(""); setConfirm("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Wijzigen mislukt");
    } finally { setBusy(false); }
  };

  return (
    <div className="card-flat p-6 max-w-xl" data-testid="password-tab">
      <div className="flex items-start gap-4 mb-5">
        <div className="w-10 h-10 rounded-md bg-[#5BC044]/15 text-[#5BC044] flex items-center justify-center shrink-0">
          <KeyRound size={18} strokeWidth={1.8} />
        </div>
        <div>
          <div className="font-bold text-lg tracking-tight text-[#1B2D5C]">Wachtwoord wijzigen</div>
          <p className="text-sm text-zinc-500 mt-1">Kies een sterk wachtwoord dat je nergens anders gebruikt.</p>
        </div>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <PwField label="Huidig wachtwoord" value={current} onChange={setCurrent} show={show} testid="pw-current" />
        <PwField label="Nieuw wachtwoord (min. 6 tekens)" value={newPw} onChange={setNewPw} show={show} testid="pw-new" />
        <PwField label="Herhaal nieuw wachtwoord" value={confirm} onChange={setConfirm} show={show} testid="pw-confirm" />
        <label className="inline-flex items-center gap-2 text-sm text-zinc-600 cursor-pointer">
          <input type="checkbox" checked={show} onChange={(e) => setShow(e.target.checked)} className="accent-[#5BC044] w-4 h-4" data-testid="pw-show" />
          Wachtwoorden tonen
        </label>
        <button type="submit" disabled={busy || !current || !newPw || !confirm} className="w-full sm:w-auto h-10 px-6 bg-[#1B2D5C] hover:bg-[#142244] disabled:opacity-50 text-white rounded-md text-sm font-semibold" data-testid="pw-submit">
          {busy ? "Bezig…" : "Wachtwoord wijzigen"}
        </button>
      </form>
    </div>
  );
}

function PwField({ label, value, onChange, show, testid }) {
  return (
    <div>
      <label className="overline block mb-1.5">{label}</label>
      <input required type={show ? "text" : "password"} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 px-3 border border-zinc-300 rounded-md text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#5BC044]/40 focus:border-[#5BC044]"
        data-testid={testid} autoComplete="new-password" />
    </div>
  );
}

function TrashSection() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [zoom, setZoom] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/invoices/trash");
      setItems(r.data);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const restore = async (id) => {
    try {
      await api.post(`/invoices/${id}/restore`);
      toast.success("Hersteld");
      load();
    } catch { toast.error("Herstellen mislukt"); }
  };

  const permanentDelete = async (id) => {
    if (!confirm("Definitief verwijderen? Dit kan niet ongedaan worden gemaakt.")) return;
    try {
      await api.delete(`/invoices/${id}/permanent`);
      toast.success("Definitief verwijderd");
      load();
    } catch { toast.error("Verwijderen mislukt"); }
  };

  return (
    <>
      <div className="overline mt-8 mb-3">Prullenbak</div>
      <div className="card-flat p-5" data-testid="trash-section">
        <button
          onClick={() => setOpen((v) => !v)}
          className="w-full flex items-center gap-4 text-left"
          data-testid="trash-toggle"
        >
          <div className="w-10 h-10 rounded-md bg-zinc-100 text-zinc-600 flex items-center justify-center shrink-0">
            <Trash2 size={18} strokeWidth={1.8} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-[15px] tracking-tight">Verwijderde facturen</div>
            <p className="text-sm text-zinc-500 mt-1">{items.length === 0 ? "Geen items in de prullenbak." : `${items.length} factuur${items.length === 1 ? "" : "en"} — herstel of verwijder definitief.`}</p>
          </div>
          <span className="text-xs font-mono text-zinc-500 shrink-0">{open ? "Sluiten" : "Bekijken"}</span>
        </button>

        {open && items.length > 0 && (
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="trash-list">
            {items.map((inv) => (
              <div key={inv.id} className="border border-zinc-200 rounded-md overflow-hidden" data-testid={`trash-item-${inv.id}`}>
                <button onClick={() => setZoom(inv.image_url)} className="block w-full aspect-[4/5] bg-zinc-100 bg-cover bg-center" style={{ backgroundImage: `url(${inv.image_url})` }} />
                <div className="p-3">
                  <div className="font-semibold text-sm truncate text-[#1B2D5C]">{inv.description || "Factuur"}</div>
                  <div className="text-xs text-zinc-500 truncate">{inv.restaurant_name || "—"} · {inv.chef_name}</div>
                  <div className="text-xs text-zinc-400 font-mono mt-1">{formatDate(inv.created_at)}</div>
                  {inv.amount ? <div className="font-mono font-bold text-[#5BC044] text-sm mt-1">{formatEUR(inv.amount)}</div> : null}
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      onClick={() => restore(inv.id)}
                      className="flex-1 h-8 inline-flex items-center justify-center gap-1.5 text-xs font-semibold border border-zinc-300 hover:border-[#5BC044] hover:text-[#5BC044] rounded-md"
                      data-testid={`restore-invoice-${inv.id}`}
                    >
                      <RotateCcw size={12} /> Herstel
                    </button>
                    <button
                      onClick={() => permanentDelete(inv.id)}
                      className="flex-1 h-8 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-red-600 border border-red-200 hover:bg-red-50 rounded-md"
                      data-testid={`permanent-delete-${inv.id}`}
                    >
                      <Trash2 size={12} /> Verwijder
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {open && items.length === 0 && (
          <div className="mt-5 py-10 text-center text-sm text-zinc-400 border border-dashed border-zinc-200 rounded-md">
            <Receipt size={26} className="mx-auto mb-2 text-zinc-300" />
            De prullenbak is leeg.
          </div>
        )}
      </div>

      {zoom && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => setZoom(null)} data-testid="trash-zoom">
          <img src={zoom} alt="" className="max-h-[90vh] max-w-[90vw] object-contain" />
        </div>
      )}
    </>
  );
}
